import { useEffect, useMemo, useState } from 'react';
import { useParams, useLocation } from 'wouter';
import { useNav } from '@/lib/nav';
import routes from '@/lib/routes';
import Header from '@/components/layout/header';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/auth/AuthProvider';
import { useProjectsStore } from '@/store/projects';
import { useFigureCompositionStore } from '@/store/figure-composition';
import { FigureCanvasWrapper } from '@/components/figure-composition/FigureCanvasWrapper';
import { PropertiesPanel } from '@/components/figure-composition/PropertiesPanel';
import { LibrarySidebar } from '@/components/figure-composition/LibrarySidebar';
import { ResizablePanels } from '@/components/layout/resizable-panels';
import { Loader2, CheckCircle2, AlertCircle, Circle, WifiOff } from 'lucide-react';
import { authorizedFetch } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { Card } from '@/components/ui/card';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import UnsavedChangesDialog from '@/components/editor/UnsavedChangesDialog';

export default function ProjectFigureEditorPage() {
  const { projectId, figureId } = useParams<{ projectId?: string; figureId?: string }>();
  const [location] = useLocation();
  const { go } = useNav();
  const { user } = useAuth();
  const { setCurrentProject } = useProjectsStore();
  const {
    currentComposition,
    isLoading,
    error,
    loadComposition,
    initNewComposition,
    saveComposition,
    saveStatus,
    lastSaveError,
    isDraft,
    isDirty,
    loadDraftIfAny,
    clearDraft,
    figureId: storeFigureId,
    setOnlineStatus,
  } = useFigureCompositionStore();
  const { toast } = useToast();
  const [showDraftBanner, setShowDraftBanner] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);

  // Use shared online status hook and sync to store
  const isOnline = useOnlineStatus();

  const isNewRoute = location.endsWith('/new');

  const queryParams = useMemo(() => {
    try {
      const url = new URL(window.location.href);
      return {
        plotId: url.searchParams.get('plotId'),
        plotVersionId: url.searchParams.get('plotVersionId'),
      };
    } catch {
      return { plotId: null, plotVersionId: null };
    }
  }, [location]);

  // Sync online status to store whenever it changes
  useEffect(() => {
    setOnlineStatus(isOnline);
  }, [isOnline, setOnlineStatus]);

  useEffect(() => {
    if (!user) {
      go(routes.home());
      return;
    }
    if (!projectId) return;

    authorizedFetch(`/api/projects/${projectId}`)
      .then((res) => res.json())
      .then((project) => setCurrentProject(project))
      .catch(() => go(routes.dashboard()));

    if (isNewRoute) {
      // Check for existing draft
      const draft = loadDraftIfAny(projectId);
      if (draft && !draftRestored) {
        setShowDraftBanner(true);
        return; // Wait for user to decide
      }

      // Initialize new composition (client-side only, no DB write)
      // Always use saved server data - no figureJsonOverride
      initNewComposition(projectId, {
        initialPlot: queryParams.plotId
          ? {
              plotId: queryParams.plotId,
              plotVersionId: queryParams.plotVersionId || undefined,
            }
          : undefined,
      });
    } else if (figureId) {
      loadComposition(figureId)
        .then(() => {
          // Check for local draft after loading
          // Note: loadComposition already checks for drafts, but we need to show banner
          // if draft was found and is newer than server version
          const draft = loadDraftIfAny(projectId, figureId);
          const comp = useFigureCompositionStore.getState().currentComposition;
          const isDirty = useFigureCompositionStore.getState().isDirty;
          
          // Show banner if draft exists and is newer, or if composition is marked dirty
          const compUpdatedAt = comp?.updatedAt instanceof Date ? comp.updatedAt.toISOString() : (typeof comp?.updatedAt === 'string' ? comp.updatedAt : comp?.updatedAt ? new Date(comp.updatedAt).toISOString() : null);
          if ((draft && comp && compUpdatedAt && draft.updatedAt > compUpdatedAt) || isDirty) {
            setShowDraftBanner(true);
          }
        })
        .catch((err) => {
          toast({
            title: 'Failed to load figure',
            description: err instanceof Error ? err.message : 'Unknown error',
            variant: 'destructive',
          });
          go(routes.project.figures.list(projectId));
        });
    }
  }, [projectId, figureId, isNewRoute, user, setCurrentProject, loadComposition, initNewComposition, loadDraftIfAny, queryParams.plotId, queryParams.plotVersionId, go, toast, draftRestored]);

  const handleRestoreDraft = () => {
    if (!projectId) return;
    const draft = loadDraftIfAny(projectId, figureId);
    if (draft) {
      // The store's loadComposition already handles restoring drafts for existing figures
      // For new route, we need to manually restore
      if (isNewRoute) {
        // Directly set state without calling initNewComposition first (more efficient)
        useFigureCompositionStore.setState({
          currentComposition: draft.composition,
          projectId,
          figureId: null, // Still a draft
          isDraft: true,
          isDirty: true,
          saveStatus: 'unsaved-draft',
          lastSavedAt: null,
          error: null,
          isLoading: false,
          isCreating: false,
        });
      } else {
        // For existing figure, reload composition which will restore draft
        loadComposition(figureId!).catch(() => {
          // Error already handled in loadComposition
        });
      }
      setShowDraftBanner(false);
      setDraftRestored(true);
    }
  };

  const handleDiscardDraft = () => {
    if (!projectId) return;
    clearDraft(projectId, figureId);
    setShowDraftBanner(false);
    if (isNewRoute && !draftRestored) {
      // Always use saved server data - no figureJsonOverride
      initNewComposition(projectId, {
        initialPlot: queryParams.plotId
          ? {
              plotId: queryParams.plotId,
              plotVersionId: queryParams.plotVersionId || undefined,
            }
          : undefined,
      });
    }
  };

  const handleSave = async () => {
    if (!currentComposition) return;
    
    // Check if offline
    if (!isOnline) {
      toast({
        title: 'Cannot save while offline',
        description: 'Your changes are stored locally and will be saved when you reconnect.',
        variant: 'destructive',
      });
      return;
    }

    try {
      await saveComposition(currentComposition);
      
      // If this was a draft, navigate to the new figure URL
      if (isDraft && storeFigureId && projectId) {
        go(routes.project.figures.edit(projectId, storeFigureId));
      }
      
      toast({
        title: 'Figure saved',
        description: 'Your changes have been saved.',
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      toast({
        title: 'Failed to save',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const hasUnsavedChanges = useMemo(() => {
    return isDirty || saveStatus === 'unsaved-draft' || saveStatus === 'unsaved-changes';
  }, [isDirty, saveStatus]);

  const saveStatusDisplay = useMemo(() => {
    // Store's saveStatus is now synced with online status via setOnlineStatus
    if (saveStatus === 'saving') {
      return { icon: Loader2, text: 'Saving...', className: 'text-muted-foreground' };
    }
    if (saveStatus === 'saved') {
      return { icon: CheckCircle2, text: 'All changes saved', className: 'text-green-600' };
    }
    if (saveStatus === 'error') {
      return { icon: AlertCircle, text: 'Save failed – Retry', className: 'text-destructive' };
    }
    if (saveStatus === 'offline') {
      return { icon: WifiOff, text: 'Offline – changes stored locally', className: 'text-amber-600' };
    }
    if (saveStatus === 'unsaved-draft') {
      return { icon: Circle, text: 'Unsaved draft', className: 'text-amber-600' };
    }
    if (saveStatus === 'unsaved-changes') {
      return { icon: Circle, text: 'Unsaved changes', className: 'text-amber-600' };
    }
    return null;
  }, [saveStatus]);

  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
  // Left sidebar: same calculation as Lumi
  const libraryWidth = Math.min(340, Math.max(280, Math.floor(windowWidth * 0.18)));
  // Right sidebar: default 360px
  const editorWidth = 360;
  // Canvas: flex-fills remaining space, min 500px
  const canvasWidth = Math.max(500, windowWidth - libraryWidth - editorWidth - 100);

  const panels = [
    {
      id: 'library',
      minWidth: 280,
      maxWidth: 400,
      defaultWidth: libraryWidth,
      children: currentComposition ? <LibrarySidebar projectId={currentComposition.projectId} /> : null
    },
    {
      id: 'canvas',
      minWidth: 500,
      maxWidth: 2000,
      defaultWidth: canvasWidth,
      children: currentComposition ? (
        <FigureCanvasWrapper
          onSave={handleSave}
          isSaving={saveStatus === 'saving'}
          unsaved={hasUnsavedChanges}
          unsavedLabel={saveStatusDisplay?.text}
          isOffline={!isOnline}
          isEmpty={currentComposition.panels.every(p => !p.plotId)}
        />
      ) : null
    },
    {
      id: 'editor',
      minWidth: 300,
      maxWidth: 500,
      defaultWidth: editorWidth,
      children: <PropertiesPanel />
    }
  ];

  return (
    <div className="flex flex-col h-screen w-screen" data-testid="figure-editor-page">
      <Header />
      
      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        open={showDraftBanner}
        isNewRoute={isNewRoute}
        onUseLocal={handleRestoreDraft}
        onDiscard={handleDiscardDraft}
      />

      {/* Offline banner */}
      {!isOnline && (
        <div className="px-4 py-2 border-b border-amber-500 bg-amber-50">
          <div className="max-w-screen-2xl mx-auto flex items-center gap-2">
            <WifiOff className="h-4 w-4 text-amber-600" />
            <p className="text-sm text-amber-900 font-medium">
              You're offline. Changes are stored locally and will be saved when you reconnect.
            </p>
          </div>
        </div>
      )}
      
      {(error || lastSaveError) && (
        <div className="px-4 py-2 border-b border-destructive bg-destructive/10">
          <div className="max-w-screen-2xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-destructive" />
              <p className="text-sm text-destructive font-medium">{error || lastSaveError}</p>
            </div>
            {lastSaveError && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSave}
              >
                Retry Save
              </Button>
            )}
          </div>
        </div>
      )}

      {isLoading && !currentComposition && (
        <div className="flex-1 flex items-center justify-center">
          <div className="flex items-center gap-2">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="text-muted-foreground">
              {isNewRoute ? 'Initializing figure...' : 'Loading figure...'}
            </span>
          </div>
        </div>
      )}

      {!isLoading && currentComposition && (
        <ResizablePanels 
          panels={panels} 
          storageKey="figure-editor-panel-widths"
          flexPanelId="canvas"
        />
      )}

      {!isLoading && !currentComposition && !isNewRoute && (
        <div className="flex-1 flex items-center justify-center">
          <Card className="p-12 text-center">
            <p className="text-muted-foreground mb-4">Figure not found</p>
            <Button
              onClick={() => go(routes.project.figures.list(projectId!))}
            >
              Back to Multi-panel Figures
            </Button>
          </Card>
        </div>
      )}
    </div>
  );
}
